/**
 * Desktop-only MCP helpers (U4 split out of McpIntegration.tsx): OS-keychain
 * token storage (Windows Credential Manager via Tauri IPC) and the Claude
 * Desktop config writer. Everything here crosses the IPC bridge — callers
 * must gate on `isDesktopApp()` first; on web these invokes reject.
 *
 * Token storage is workspace-keyed (v0.1.14 hardening Tier 2) so one OS user
 * can run multiple Aletheia workspaces side-by-side; `workspaceId` is the
 * Clerk user id under the current single-org-per-user model.
 */

import {
  configureClaudeDesktop,
  deleteMcpToken,
  getInstallPath,
  getLocalMcpUrl,
  getTokenStatus,
  revealMcpToken,
  storeMcpToken,
} from '@/desktop/desktopIpc';
import { configureDesktopDirectBackend } from '@/desktop/directBackend';

export type { ConfigureClaudeResult } from '@/desktop/desktopIpc';

/** Camel-cased view of the keychain's `get_token_status` result. */
export interface KeychainStatus {
  hasToken: boolean;
  /** First-4 + last-4 form. NEVER the raw token. */
  masked: string | null;
}

/** Everything the token panel hydrates on mount, in one IPC round-trip. */
export interface DesktopMcpSnapshot extends KeychainStatus {
  installPath: string;
  localUrl: string;
}

export async function loadDesktopMcpSnapshot(workspaceId: string): Promise<DesktopMcpSnapshot> {
  const [status, installPath, localUrl] = await Promise.all([
    getTokenStatus(workspaceId),
    getInstallPath(),
    getLocalMcpUrl(),
  ]);
  return { hasToken: status.has_token, masked: status.masked, installPath, localUrl };
}

/** Hand a freshly-minted plaintext token to the OS credential store, then
 *  re-read the masked status — the caller drops the plaintext immediately. */
export async function storeTokenInKeychain(
  workspaceId: string,
  token: string,
): Promise<KeychainStatus> {
  await storeMcpToken(workspaceId, token);
  const status = await getTokenStatus(workspaceId);
  return { hasToken: status.has_token, masked: status.masked };
}

/** "Forget this device" — delete the stored token and re-read status. */
export async function forgetDeviceToken(workspaceId: string): Promise<KeychainStatus> {
  await deleteMcpToken(workspaceId);
  const status = await getTokenStatus(workspaceId);
  return { hasToken: status.has_token, masked: status.masked };
}

/** Reveal the stored plaintext (the caller auto-hides it after 30 s). */
export const revealStoredToken = revealMcpToken;

/** Config writer: merge Aletheia's entry into `claude_desktop_config.json`,
 *  preserving any other MCP servers the user has configured. */
export const writeClaudeDesktopConfig = configureClaudeDesktop;

/** Switch desktop chat over to direct-to-Azure routing once a token exists
 *  (v0.1.14 — replaces the unconditional boot-time call in main.tsx). */
export const engageDirectBackend = configureDesktopDirectBackend;
